/**
 * Companies service - handles API calls for company management
 */

import { apiFetch } from "./apiClient";

const API_URL = import.meta.env.VITE_AUTH_API_URL;

export interface Company {
  id: string;
  name: string;
  created_at: string;
  is_active: boolean;
}

/**
 * Get all companies
 */
export async function getCompanies(): Promise<Company[]> {
  const response = await apiFetch(`${API_URL}/companies`);

  if (!response.ok) {
    throw new Error('Failed to fetch companies');
  }

  const data = await response.json();
  return data.data || [];
}

/**
 * Get a company by ID
 */
export async function getCompanyById(id: string): Promise<Company> {
  const response = await apiFetch(`${API_URL}/companies/${id}`);

  if (!response.ok) {
    throw new Error('Failed to fetch company');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Create a new company
 */
export async function createCompany(companyData: { name: string }): Promise<Company> {
  const response = await apiFetch(`${API_URL}/companies`, {
    method: 'POST',
    body: JSON.stringify(companyData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create company');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Update a company.
 * Supports partial updates: name and/or is_active.
 * At least one field must be provided.
 */
export async function updateCompany(
  id: string,
  companyData: { name?: string; is_active?: boolean },
): Promise<Company> {
  const response = await apiFetch(`${API_URL}/companies/${id}`, {
    method: 'PUT',
    body: JSON.stringify(companyData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update company');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Delete a company (hard delete — cascades to branches, devices, users)
 */
export async function deleteCompany(id: string): Promise<void> {
  const response = await apiFetch(`${API_URL}/companies/${id}`, { method: 'DELETE' });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete company');
  }
}

// ─── Tipos del resumen de eliminación ────────────────────────────────────────

export interface CompanyDeleteSummary {
  branches: number;
  owners: number;    // role_id = 2
  managers: number;  // role_id = 3
  devices: number;
}

/**
 * Obtiene el resumen de recursos dependientes de una empresa.
 * Se usa para mostrar advertencias antes de eliminar.
 */
export async function getCompanyDeleteSummary(
  companyId: string
): Promise<CompanyDeleteSummary> {
  const [branchesRes, ownersRes, managersRes, devicesRes] = await Promise.all([
    apiFetch(`${API_URL}/branches?company_id=${companyId}`),
    apiFetch(`${API_URL}/users?company_id=${companyId}&role_id=2`),
    apiFetch(`${API_URL}/users?company_id=${companyId}&role_id=3`),
    apiFetch(`${API_URL}/devices?company_id=${companyId}`),
  ]);

  const [branchesData, ownersData, managersData, devicesData] = await Promise.all([
    branchesRes.ok  ? branchesRes.json()  : Promise.resolve({ data: [] }),
    ownersRes.ok    ? ownersRes.json()    : Promise.resolve({ data: [] }),
    managersRes.ok  ? managersRes.json()  : Promise.resolve({ data: [] }),
    devicesRes.ok   ? devicesRes.json()   : Promise.resolve({ data: [] }),
  ]);

  return {
    branches: (branchesData.data ?? []).length,
    owners:   (ownersData.data   ?? []).length,
    managers: (managersData.data ?? []).length,
    devices:  (devicesData.data  ?? []).length,
  };
}
